import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/cn";

type Variant = "primary" | "secondary" | "accent" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary-500 text-background-50 hover:bg-primary-600 focus-visible:ring-primary-400",
  secondary:
    "bg-secondary-500 text-background-50 hover:bg-secondary-600 focus-visible:ring-secondary-400",
  accent:
    "bg-accent-500 text-foreground-950 hover:bg-accent-600 focus-visible:ring-accent-400",
  outline:
    "border border-foreground-200 bg-background-50 text-foreground-900 hover:bg-background-100 hover:border-foreground-300",
  ghost: "text-foreground-800 hover:bg-background-100",
  danger: "bg-primary-700 text-background-50 hover:bg-primary-800",
};

const sizes: Record<Size, string> = {
  sm: "px-3.5 py-2 text-sm gap-1.5 rounded-md",
  md: "px-5 py-2.5 text-sm gap-2 rounded-md",
  lg: "px-6 py-3 text-base gap-2 rounded-md",
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
  icon?: string;
  iconPosition?: "left" | "right";
}

interface ButtonProps extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  to?: string;
}

export default function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  icon,
  iconPosition = "right",
  to,
  ...rest
}: ButtonProps) {
  const classes = cn(
    "group inline-flex items-center justify-center whitespace-nowrap font-label font-medium transition-all duration-200 cursor-pointer hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed",
    variants[variant],
    sizes[size],
    className
  );

  const content = (
    <>
      {icon && iconPosition === "left" && (
        <i className={cn(icon, "text-base transition-transform duration-200 group-hover:-translate-x-0.5")} aria-hidden="true" />
      )}
      {children}
      {icon && iconPosition === "right" && (
        <i className={cn(icon, "text-base transition-transform duration-200 group-hover:translate-x-0.5")} aria-hidden="true" />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  );
}